import {
  useState, createContext, useContext, ReactElement, ReactNode,
  useMemo,
  useCallback,
} from 'react';
import styled, { keyframes } from 'styled-components';
import {
  AnnotationAlertIcon,
  AnnotationCheckIcon,
  AnnotationInfoIcon,
  AnnotationXIcon,
  CloseIcon,
} from '~/icons';
import { colors } from '~/styles/colors';

type ToastType = 'success' | 'warning' | 'error' | 'info';
type Toast = { id: number; title: string; message: string; type: ToastType };

interface ToastContext {
  toast: (title: string, message: string, type: ToastType) => void;
}

const slide = keyframes`
  0% { opacity: 0; transform: translateX(500px); }
  10% { opacity: 1; transform: translateX(0); }
  90% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(500px); }
`;

const colorsType = {
  warning: colors.state.warning.base,
  error: colors.state.danger.base,
  success: colors.state.success.base,
  info: colors.state.info.base,
};

const ToastParentContainer = styled.div`
  margin: 8px 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 10000000000000;

  @media (max-width: 768px) {
    bottom: 0;
    top: auto;
    margin: 0px 8px 8px 0;
  }
`;

const ToastContainer = styled.div<{ type: ToastType, message: string, index: number }>`
  height: 76px;
  min-width: 361px;
  display: flex;
  align-items: start;
  justify-content: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;

  animation: ${slide} 3s ease-in-out forwards;
  color: ${colors.text['white-0']};
  background-color: ${({ type }) => colorsType[type]};

  & > div {
    display: flex;
    flex-direction: column;
    gap: 12px;

    span {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0%;
    }
  }

  & > button {
    padding: 0 6px;
    margin: 0;
    margin-left: auto;
    border: 0;
    background-color: transparent;
    cursor: pointer;
  }
`;

const icons = {
  success: AnnotationCheckIcon,
  warning: AnnotationAlertIcon,
  error: AnnotationXIcon,
  info: AnnotationInfoIcon,
};

const ToastContext = createContext<ToastContext | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((n) => n.id !== id));
  };

  const toast = useCallback((title: string, message: string, type: ToastType) => {
    const id = Date.now();
    setToasts((prev) => [...prev, {
      id, message, type, title,
    }]);
    setTimeout(() => removeToast(id), 3000);
  }, []);

  const values = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={values}>
      {children}
      {toasts.length > 0 ? (
        <ToastParentContainer>
          {toasts.map(({
            id, message, type, title,
          }, index) => {
            const Icon = icons[type];
            return (
              <ToastContainer key={id} type={type} message={message} index={index}>
                <Icon />
                <div>
                  <span>{title}</span>
                  <span>{message}</span>
                </div>
                <button type="button" onClick={() => removeToast(id)}>
                  <CloseIcon color={colors.icon['white-0']} size="12" />
                </button>
              </ToastContainer>
            );
          })}
        </ToastParentContainer>
      ) : null}
    </ToastContext.Provider>
  );
};

/**
 *
 * Usage example in a component
 * ```
 * import { useToast } from 'contexts/newToastContext';
 *
 * const { toast } = useToast();
 *
 * toast('Sucesso!', 'Alterações realizadas com sucesso!', 'success');
 * toast('Aviso!', 'Aviso!', 'warning');
 * toast('Erro!', 'Ocorreu um erro ao salvar alterações', 'error');
 * ```
 * @return {*}  {ToastContext}
 */
export const useToast = (): ToastContext => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast deve ser usado dentro de um ToastProvider');
  }
  return context;
};
